import { format } from "date-fns";
import { Download } from "lucide-react";

import { _truncateFilename } from "@/components/helpers/string-utils";
import { Flex } from "@/components/layout/flexbox";
import { Mp3Player } from "@/components/pieces/audio/Mp3Player";
import { Button } from "@/components/ui/button";

interface SessionRecordingProps {
  recordingUrl?: string;
  onTimeChange?: (event: Event, duration: number) => void;
  currentTime?: number;
  scenarioName: string;
  user: {
    name?: string | null;
    email?: string | null;
  };
}

export default function SessionRecording({
  recordingUrl,
  onTimeChange,
  currentTime = 0,
  scenarioName,
  user,
}: SessionRecordingProps) {
  if (!recordingUrl) {
    return <></>;
  }

  const _handleDownload = async (audioUrl: string) => {
    try {
      const response = await fetch(audioUrl);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;

      const today = new Date();
      const dateString = format(today, "yyyy-MM-dd");

      // Truncate scenario name to max 25 characters
      const truncatedScenarioName = _truncateFilename(scenarioName);
      const cleanScenarioName =
        truncatedScenarioName?.replace(/[^a-zA-Z0-9]/g, "_") || "scenario";

      // Format filename as [UserName]_[ScenarioTitle]_[YYYY-MM-DD].mp3
      const userInfo = user?.name || user?.email || "unknown_user";
      link.download = `${userInfo}_${cleanScenarioName}_${dateString}.mp3`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <Flex
      className="bas round-corners"
      backgroundColor="#FFF"
      px={24}
      py={3}
      alignItems="center"
      flexDirection={["column", "row"]}
      justifyContent="space-between"
      width={["100%", "auto"]}
      mt={16}
      css={{
        position: "sticky",
        top: 70,
        zIndex: 9,
      }}
    >
      <Mp3Player
        src={recordingUrl}
        showJumpControls={true}
        onTimeChange={onTimeChange}
        currentTime={currentTime}
        showPlaybackSpeed
      />
      <Button
        variant="outline"
        intent="secondary"
        className="w-[40px] h-[40px] ml-3"
        onClick={() => _handleDownload(recordingUrl)}
      >
        <Download />
      </Button>
    </Flex>
  );
}
